import { Save, SaveAs } from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useContext, useEffect, useState } from 'react';
import ModalBase from '../../../common/components/cards/ModalBase';
import { OverallDataContext } from '../../pages/CalculatorPage';
import EllipsisLoader from '../loaders/EllipsisLoader';
import CopyToClipboard from './CopyToClipboard';

const pseudoInputFieldStyles = {
  width: '100%',
  padding:  '8px 12px',
  boxSizing: 'border-box',
  borderRadius: '24px',
  backgroundColor: '#ffffffdf',
  border: '1px solid rgba(0, 0, 0, 0.3)',
  color: 'rgba(0, 0, 0, 0.3)'
}

export default function SaveCalcModal() {
    // Context data
    const [metadata, setMetadata] = useContext(OverallDataContext).metadata;
    const handleSubmit = useContext(OverallDataContext).handleSubmitButtonPress;

    // UI flags
    const isLoading = useContext(OverallDataContext).isLoading;
    const [shouldSubmit, setShouldSubmit] = useState(false);
    const [wasEdited, setEdited] = useState(false);
    const [isInvalidEmailError, setEmailError] = useState(false);
    const [isEmptyError, setEmptyError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Input fields
    const [referenceId, setReferenceId] = useState('');
    const [title, setTitle] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [dateCreated, setDateCreated] = useState('');
    const [dateEdited, setDateEdited] = useState('');
    
    // Modal controls
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);

    const formatDate = (rawDate) => {
        return new Date(rawDate).toLocaleString('en-SG', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false, // optional
            timeZone: 'Asia/Singapore' // optional, for certainty
        }).replace(',', '');
    };

    const checkEmailFormat = (email) => {
        if (email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setEmailError(!emailRegex.test(email));
        }
    }

    const handleEdits = () => {
        setEdited(true);
        setShouldSubmit(false);
    }

    const handleSave = () => {
        if (!title || !email) {
            setEmptyError(true);
            setErrorMsg('Please fill in all fields before saving!');
            setShouldSubmit(false);  // Prevent submission
        } else {
            setEmptyError(false);
            setErrorMsg('');
            setMetadata(md => ({
                ...md,
                referenceId: referenceId,
                reportTitle: title,
                creatorEmail: email,
                creatorName: name,
            }));
            setShouldSubmit(true);  // Trigger effect after metadata update
        }
    }

    useEffect(() => {
        if (shouldSubmit) {
            handleSubmit(true);
            setShouldSubmit(false);
        }
    }, [metadata]);

    useEffect(() => {
        // Reset UI flags
        setEdited(false);
        setEmptyError(false);
        setErrorMsg('');

        // Initialise input fields with fetched metadata
        setReferenceId(metadata?.rid || '');
        setTitle(metadata?.reportTitle || '');
        setEmail(metadata?.creatorEmail || '');
        setName(metadata?.creatorName || '');
        setDateCreated(metadata?.dateCreated || '');
        setDateEdited(metadata?.dateLastEdited || '');
    }, [metadata, open])
    
    return (
        <ModalBase 
            title="Save Calculation Session"
            open={open}
            setOpen={setOpen}
            opener={
                <Button 
                    className='pill_button secondary_button' 
                    disabled={isLoading} 
                    onClick={handleOpen}
                >
                    {referenceId ? <SaveAs /> : <Save />}&ensp;
                    {referenceId ? 'Update' : 'Save'} Session
                </Button>
            }
            >   
            <Stack spacing={2}>
                <Typography variant='body2'>
                    Please fill in the details below to save your calculation session. The email address you provide will be used to send you a copy of the report reference ID, and to notify you of any updates or changes to the calculation.
                </Typography>

                { referenceId &&
                <Box>
                    <label htmlFor='referenceID_input' >
                        Reference ID
                    </label>
                    <Box sx={pseudoInputFieldStyles} >
                        <CopyToClipboard contentToCopy={referenceId}>
                            &ensp;{referenceId}
                        </CopyToClipboard>
                    </Box>
                </Box>
            }
                <Box>
                    <label htmlFor='reporttitle_input' className='required_input'>
                        Project Title
                    </label>
                    <TextField 
                        name='reporttitle_input' id='reporttitle_input' 
                        type='text'
                        fullWidth
                        value={title}
                        onChange={(e) => {setTitle(e.target.value)}}
                        error={(isEmptyError && title==='')}
                        onBlur={handleEdits}
                    />
                </Box>
                <Box>
                    <label htmlFor='email_input' className='required_input'>
                        Email
                    </label>
                    <TextField 
                        name='email_input' id='email_input' 
                        type='text'
                        fullWidth
                        value={email}
                        onChange={(e) => {setEmail(e.target.value.trim().toLowerCase())}}
                        error={(isEmptyError && email==='') || isInvalidEmailError}
                        helperText={isInvalidEmailError ? 'Please enter a valid email address!' : ''}
                        onBlur={() => {checkEmailFormat(email); handleEdits();}}
                    />
                </Box>
                {/* <Box>
                    <label htmlFor='name_input' >
                        Contact Name
                    </label>
                    <TextField 
                        name='name_input' id='name_input' 
                        type='text'
                        fullWidth
                        value={name}
                        onChange={(e) => {setName(e.target.value)}}
                        error={(isEmptyError && name==='')}
                        onBlur={handleEdits}
                    />
                </Box> */}

                { errorMsg &&
                <Alert severity="error">
                    {errorMsg}
                </Alert>
                }

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'right' }}>
                    {
                        isLoading ?
                        <Button className='pill_button secondary_button'>
                            &nbsp;<EllipsisLoader />&nbsp;
                        </Button>
                        :
                        <Button 
                            className={`pill_button ${wasEdited ? 'submit_button': 'disabled_button'}`} 
                            disabled={!wasEdited}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    }
                    { (dateCreated || dateEdited) &&
                        <Typography variant='caption' color="text.secondary">
                            Report created on: {formatDate(dateCreated)}
                            <br/>
                            Report last edited on: {formatDate(dateEdited)}
                        </Typography>
                    }
                </Box>
            </Stack>
        </ModalBase>
    )
}